import { IMessage, MessageType } from "@common/models/Message";
import { IResponse } from "@common/models/Response";
import { MessageBoxBtnGroup } from "@components/general/MessageBox";
import { appConfig } from "@config/appConfig";
import { Culture } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import { store } from "@misc/Reducer";
import Notification from "@service/Notification";
import { linkScriptToHeader } from "@utilize/Init";
import { Account } from "./Account";
import { Authentication } from "./Authentication";
import Config from "./Config";
import Translate from "./Translate";

export interface IRequestHeader {
  [name: string]: string;
}

export interface IApiRequest<T> extends Promise<T> {
  abort?: () => void;
  xhr?: XMLHttpRequest;
}

export interface IOptions<T> {
  captchaRequierd?: boolean;
  data?: T;
  headers?: IRequestHeader;
  ignoreNotificationCodes?: IMessage[];
  raiseError?: boolean;
  signal?: AbortSignal;
  authenticationId?: string;
  callBackTrigger?: boolean;
}

// TODO Integrate arguments to one arguments
export interface IApi {
  del<T, U>(edge: string, options?: IOptions<T>): IApiRequest<IResponse<U>>;
  get<T, U>(edge: string, options?: IOptions<T>): IApiRequest<IResponse<U>>;
  post<T, U>(edge: string, options?: IOptions<T>): IApiRequest<IResponse<U>>;
  put<T, U>(edge: string, options?: IOptions<T>): IApiRequest<IResponse<U>>;
  upload<T, U>(edge: string, options?: IOptions<T>): IApiRequest<IResponse<U>>;
}

export const Api: IApi = (() => {

  const getendpoint = () => {
    const { env, basePath } = appConfig;
    const endpoint = env === 'Development' ? `${basePath}/api` : '/api';
    return endpoint;
  }

  const get = <T, U>(edge: string, options?: IOptions<T>) => {
    const queryString = options?.data ? `?${param<T>(options.data)}` : "";
    return initFetchRequest<T, U>("GET", `${edge}${queryString}`, options);
  }

  const post = <T, U>(edge: string, options?: IOptions<T>) => {
    return initFetchRequest<T, U>("POST", edge, options, options?.data);
  }

  const put = <T, U>(edge: string, options?: IOptions<T>) => {
    return initFetchRequest<T, U>("PUT", edge, options, options?.data);
  }

  const del = <T, U>(edge: string, options?: IOptions<T>) => {
    //const queryString = options?.data ? `?${param<T>(options?.data)}` : "";
    // return initFetchRequest<T, U>("DELETE", `${edge}${queryString}`, "", headers, raiseError, signal, ignoreNotificationCodes);
    return initFetchRequest<T, U>("DELETE", edge, options, options?.data);
  }

  const upload = <T, U>(edge: string, options?: IOptions<T>) => {
    const formData = new FormData();
    if (options?.data)
      for (let fields = Object.keys(options.data), i = 0, il = fields.length; i < il; ++i) {
        const value = (options.data as any)[fields[i]];
        const fieldName = fields[i];
        // in order to upload the files properly, we should flattern the uploaded files
        if (value instanceof File) {
          formData.append(fields[i], value);
        } else if (Array.isArray(value)) {
          for (let j = 0, jl = value.length; j < jl; ++j) {
            formData.append(`${fieldName}_${j}`, value[j]);
          }
        } else if ("object" === typeof value) {
          for (let subFields = Object.keys(value), j = 0, jl = subFields.length; j < jl; ++j) {
            const subFieldName = subFields[j];
            formData.append(`${fieldName}_${subFieldName}`, value[subFieldName]);
          }
        }
      }
    return initFetchRequest<T, U>("POST", edge, options, formData as any);
  }

  const setHeaders = (headers: IRequestHeader = {}, method: string, authId: string = "", captchaToken?: string, captchaId?: string, captchaChallenge?: string) => {
    // Set api language
    headers["lang"] = Culture.getLocale().lang.toUpperCase();
    // Set content type
    if (method !== "UPLOAD")
      headers["Content-Type"] = "application/json";
    // Set auth token
    const token = Account.getToken();
    if (token)
      headers["Authorization"] = "Bearer " + token;

    if (captchaId)
      headers["Captcha-ID"] = captchaId;

    if (captchaChallenge)
      headers["Captcha-Challenge"] = captchaChallenge;

    if (captchaToken)
      headers["Captcha-Token"] = captchaToken;
    // Set session id from session storage in headers
    const transactionId = Authentication.getTransactionId(authId);
    if (transactionId)
      headers["Pragma"] = transactionId;
    return headers;
  }

  const getCaptchaToken = (edge: string): Promise<string> => {
    const promise = new Promise<string>((resolve, reject) => {
      (window as any).grecaptcha.ready(() => {
        (window as any).grecaptcha.execute(Config.get("gCaptchaCode"), { action: `api/${edge}`.toLowerCase() })
          .then((token: string) => {
            resolve(token);
          })
          .catch((e: any) => {
            reject(e)
          })
      })
    })
    return promise;
  }

  const initFetchRequest = <T, U>(method: string, edge: string, options?: IOptions<T>, data?: T): IApiRequest<IResponse<U>> => {
    const promise: IApiRequest<IResponse<U>> = new Promise<IResponse<U>>((resolve, reject) => {
      if (Config.get("captchaEnable") && options?.captchaRequierd) {
        if ('grecaptcha' in window) {
          fetchRequestWithCaptcha<T, U>(method, edge, resolve, reject, options, data)
        } else {
          if (Account.getCaptchaStatus() !== 'Failed') {
            linkScriptToHeader(`https://www.google.com/recaptcha/api.js?render=${Config.get("gCaptchaCode")}`,
              () => {
                fetchRequestWithCaptcha<T, U>(method, edge, resolve, reject, options, data);
                Account.setCaptchaStatus("Success");
              },
              () => {
                loadCaptcha<T, U>(method, edge, resolve, reject, options, data);
                Account.setCaptchaStatus("Failed");
              })
          } else {
            loadCaptcha<T, U>(method, edge, resolve, reject, options, data);
          }
        }
      } else {
        fetchRequest<T, U>(method, edge, resolve, reject, options, data);
      }
    });
    return promise;
  }

  const loadCaptcha = <T, U>(method: string, edge: string, resolve: (value: IResponse<U> | PromiseLike<IResponse<U>>) => void, reject: (reason?: any) => void, options?: IOptions<T>, data?: T, error: boolean = false) => {
    store.dispatch<IAppAction>({
      type: AppAction.Recaptcha, payload: {
        captchaPopup: {
          visible: true,
          loading: false,
          error,
          onSubmit: (captchaId, captchaChallenge) => fetchRequest<T, U>(method, edge, resolve, reject, options, data, undefined, captchaId, captchaChallenge),
        }
      }
    });
  }

  const fetchRequestWithCaptcha = <T, U>(method: string, edge: string, resolve: (value: IResponse<U> | PromiseLike<IResponse<U>>) => void, reject: (reason?: any) => void, options?: IOptions<T>, data?: T) => {
    getCaptchaToken(edge)
      .then((captchaToken) => {
        fetchRequest<T, U>(method, edge, resolve, reject, options, data, captchaToken);
      })
      .catch((e) => {
        Notification.error("recaptcha_execute_error");
        reject(e);
      });
  }

  const fetchRequest = <T, U>(method: string, edge: string, resolve: (value: IResponse<U> | PromiseLike<IResponse<U>>) => void, reject: (reason?: any) => void, options?: IOptions<T>, data?: T, captchaToken?: string, captchaId?: string, captchaChallenge?: string) => {
    fetch(`${getendpoint()}/${edge}`, {
      body: JSON.stringify(data),
      headers: setHeaders(options?.headers, method, options?.authenticationId, captchaToken, captchaId, captchaChallenge),
      signal: options?.signal,
      method
    })
      .then((res) => {
        // Save transaction id to session storage if exist in response
        const transactionId = res.headers.get("Pragma") || "";
        if (transactionId && options?.authenticationId) {
          const { lang } = Culture.getLocale();
          const expires = +(res.headers.get("Expires") || 0);
          const callBackUrl = window.location.pathname.replace(`/${lang}`, '') + window.location.search;
          Authentication.setAuth(options.authenticationId, transactionId, expires, callBackUrl);
          store.dispatch<IAppAction>({
            type: AppAction.TransactionTimeout,
            payload: {
              transactionTimeout: {
                start: true,
                timeout: expires * 1000,
                message: Translate.tr("chk_06"),
                btnTxt: MessageBoxBtnGroup.Retry,
                btnAction: () => setTimeout(() => {
                  if (options.callBackTrigger)
                    store.dispatch<IAppAction>({ type: AppAction.NewRoute, payload: { newRoute: Authentication.getCallBackUrl(options.authenticationId ?? "") } });
                }, 300)
              }
            }
          });
        }

        switch (res.status) {
          // Done
          case 200:
            const contentType = res.headers.get("Content-Type");
            if (contentType && contentType.indexOf("application/json") > -1)
              return res.json().then((response: IResponse<U>) => {
                const errorMessages: IMessage[] = response.messages?.filter(msg => msg.type === MessageType.Error);
                if (errorMessages?.length) {
                  if (options?.raiseError)
                    errorMessages.filter(msg => !options?.ignoreNotificationCodes?.some(x => x.code?.toLowerCase() === msg.code?.toLowerCase()))?.forEach(msg => Notification(msg));

                  if (errorMessages.find(e => e.code.toLowerCase() === "rsv_40") && options?.authenticationId && options?.callBackTrigger) {
                    // first redirect to call back url and then remove authentication
                    store.dispatch<IAppAction>({ type: AppAction.NewRoute, payload: { newRoute: Authentication.getCallBackUrl(options.authenticationId) } });
                    store.dispatch<IAppAction>({ type: AppAction.TransactionTimeout, payload: { transactionTimeout: { start: false } } });
                    Authentication.removeAuth(options.authenticationId);
                  }

                  reject(response);
                } else {
                  resolve(response);
                }
              });
            else
              return reject();

          // Done with No Content
          case 204:
            return resolve({} as IResponse<U>);

          case 400:
            return res.json().then((response: IResponse<U>) => {
              const errorMessages: IMessage[] = response.messages?.filter(msg => msg.type === MessageType.Error);
              if (errorMessages?.length) {
                errorMessages.filter(msg => !options?.ignoreNotificationCodes?.some(x => x.code?.toLowerCase() === msg.code?.toLowerCase()))?.forEach(msg => Notification(msg));
              } else {
                Notification({
                  code: "err_invalid_parameter",
                  type: MessageType.Error
                })
              }
              reject(response);
            });

          // unauthorize token validation
          case 401:
            return res.text().then((res) => {
              if (res === "Undefined User") {
                return reject();
              } else if (res === 'invalid_captcha_challenge') {
                loadCaptcha<T, U>(method, edge, resolve, reject, options, data, true);
              } else {
                return getRefreshTokenAndRetry<T, U>(method, edge, options, data).then(resolve).catch(reject);
              }
            });
          // return getRefreshTokenAndRetry<T, U>(method, edge, options, data).then(resolve).catch(reject);

          case 403:
            Notification.error("err_access_denied");
            return reject();

          // internal server error
          case 500:
            if (options?.raiseError)
              Notification.error("err_server");
            return reject({
              messages: [{
                code: "err_server",
                type: MessageType.Error
              }]
            });
        }
      })
      .catch(() => {
        return reject();
      });
  }

  const getRefreshTokenAndRetry = <T, U>(method: string, edge: string, options?: IOptions<T>, data?: T): Promise<any> => {
    return new Promise((resolve, reject) => {
      fetch(`${getendpoint()}/auth`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ code: Account.getCode(), refresh_token: Account.getRefreshToken() })
      })
        .then((response) => response.status === 200 ? response.json() : redirectToLogout())
        .then((auth) => {
          Account.setToken(auth.access_token);
          Account.setRefreshToken(auth.refresh_token);
          Account.setIdToken(auth.id_token);
          initFetchRequest<T, U>(method, edge, options, data).then(resolve).catch(reject);
        })
        .catch(() => {
          redirectToLogout();
          reject();
        })
    });
  }

  const redirectToLogout = () => {
    // store.dispatch<IAppAction>({ type: AppAction.RefreshTokenError, payload: { refreshTokenError: true } });
    Account.logout();
  }

  return { del, get, post, put, upload };
})();

export default Api;

function param<T>(data: T) {
  return encodeURIComponent(JSON.stringify(data));
}